'use client'

import { oembed } from '@loomhq/loom-embed'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import {
  Dropdown,
  DropdownItem,
} from '@snowball-tech/fractal/dist/src/components/Dropdown'
import { Loader } from '@snowball-tech/fractal/dist/src/components/Loader'
import { Slider } from '@snowball-tech/fractal/dist/src/components/Slider'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj, cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import {
  UilFileDownload as DownloadIcon,
  UilPause as PauseIcon,
  UilPlay as PlayIcon,
} from '@tooni/iconscout-unicons-react'
import formatDuration from 'format-duration'
import getVideoId from 'get-video-id'

import {
  type Dispatch,
  type MouseEvent,
  type SetStateAction,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useAsync } from 'react-async-hook'
import YouTube, { type YouTubeEvent } from 'react-youtube'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isNumber from 'lodash/fp/isNumber'
import throttle from 'lodash/fp/throttle'

import type { MediaTypes, Translator } from '../../types'

import useLogger from '../../hooks/useLogger'
import useTranslations from '../../hooks/useTranslations'
import useUrls from '../../hooks/useUrls'
import DisplayError from '../DisplayError'
import Link from '../Link'

const PLAYBACK_RATES = [0.75, 1, 1.2, 1.5, 2]

type Props = {
  type: Omit<MediaTypes, 'image'>
  url: string
  className?: string
  duration?: number
  fullWidth?: boolean
  id?: string
  interactive?: boolean
  isLoading?: boolean
  setIsLoading?: Dispatch<SetStateAction<boolean>>
  withLoader?: boolean
  onLoaded?: (
    event: SyntheticEvent<HTMLAudioElement | HTMLVideoElement> | YouTubeEvent,
  ) => void
}
export default function MediaDisplay({
  className = '',
  duration: initialDuration,
  fullWidth = true,
  id,
  interactive = true,
  isLoading = false,
  onLoaded,
  setIsLoading,
  type,
  url,
  withLoader = true,
}: Props) {
  let $t: Translator | null = null
  // When using this component in the BlockNote editor it randomly happens that
  // React complains about the NextIntl context not being found, though the
  // translation is working fine.
  // To avoid the error in the console, simply silently swallow it.
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    $t = useTranslations()
  } catch {
    // Nothing to do here.
  }

  const logger = useLogger()

  const assetsURLs = useUrls()

  const [error, setError] = useState('')

  const audioPlayerRef = useRef<HTMLAudioElement>(null)
  const [currentPlayingTime, setCurrentPlayingTime] = useState(0)
  const [duration, setDuration] = useState(initialDuration ?? 0)
  const updatePlayingTime = useCallback(() => {
    setCurrentPlayingTime(audioPlayerRef.current?.currentTime ?? 0)
  }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledUpdatePlayingTime = useCallback(
    throttle(900, updatePlayingTime),
    [updatePlayingTime],
  )
  const updateDuration = useCallback(() => {
    setDuration(audioPlayerRef.current?.duration ?? initialDuration ?? 0)
  }, [initialDuration])
  useEffect(() => {
    const player = audioPlayerRef.current

    if (player !== null) {
      player.addEventListener('timeupdate', throttledUpdatePlayingTime)
      player.addEventListener('durationchange', updateDuration)
    }

    return () => {
      if (player !== null) {
        player.removeEventListener('timeupdate', throttledUpdatePlayingTime)
        player.removeEventListener('durationchange', updateDuration)
      }
    }
  }, [throttledUpdatePlayingTime, updateDuration])

  const handleSeek = (time: number | Array<number>) => {
    if (audioPlayerRef.current === null) {
      return
    }

    const newTime = isNumber(time) ? time : time[0]!

    audioPlayerRef.current.currentTime = newTime
    setCurrentPlayingTime(newTime)
  }

  const [isPlaying, setIsPlaying] = useState(false)
  const play = () => {
    if (audioPlayerRef.current !== null) {
      audioPlayerRef.current.play()
      setIsPlaying(true)
    }
  }
  const pause = () => {
    if (audioPlayerRef.current !== null) {
      audioPlayerRef.current.pause()
      setIsPlaying(false)
    }
  }
  const togglePlay = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (isPlaying) {
      pause()
    } else {
      play()
    }
  }

  const [playbackRate, setPlaybackRate] = useState(1)
  const updatePlaybackRate = (rate: number) => {
    if (audioPlayerRef.current !== null) {
      audioPlayerRef.current.playbackRate = rate
      setPlaybackRate(rate)
    }
  }

  let isUrlValid = true
  try {
    // eslint-disable-next-line no-new
    new URL(url)
  } catch {
    isUrlValid = false
  }

  const [isWaiting, setIsWaiting] = useState(false)

  const handleMediaLoaded = (
    event: SyntheticEvent<HTMLAudioElement | HTMLVideoElement>,
  ) => {
    if (isFunction(onLoaded)) {
      onLoaded(event)
    }

    setIsLoading?.(false)
    setIsWaiting(false)
  }

  const handleMediaCanPlay = () => {
    setIsLoading?.(false)
    setIsWaiting(false)
  }

  const handleMediaWaiting = () => {
    setIsWaiting(true)
  }

  const handleMediaPlaying = () => {
    setIsWaiting(false)
  }

  const handleMediaError = (
    event: SyntheticEvent<HTMLAudioElement | HTMLVideoElement>,
  ) => {
    const mediaError = (
      event.nativeEvent.target as HTMLAudioElement | HTMLVideoElement
    )?.error

    const errorMessage = mediaError?.message

    logger.error(
      `An error occured when loading the media: Code ${mediaError?.code} - "${errorMessage}"`,
      { mediaError, type, url },
      new Error(errorMessage),
    )

    setError(errorMessage || $t?.('general.unexpected_error') || ' ')
  }

  const { id: videoId = undefined, service = undefined } =
    type === 'video' ? getVideoId(url) : {}
  const isYoutube = service === 'youtube'
  const isLoom = service === 'loom'

  const videoMetadata = useAsync(async () => {
    if (type !== 'video') {
      return null
    }

    const { service: videoService } = getVideoId(url)
    if (videoService === 'loom') {
      try {
        const metadata = await oembed(url.replace('/embed/', '/share/'))
        setIsLoading?.(false)

        return metadata
      } catch (error_) {
        const actualError = error_ as Error

        logger.error(
          `An error occured when loading Loom video metadata: "${actualError.message}`,
          { type, url },
          actualError,
        )

        setError(actualError.message || $t?.('general.unexpected_error') || ' ')
      }
    }

    return null
  }, [type, url])

  const isNativeVideo = type === 'video' && !isYoutube && !isLoom

  const handleYoutubeVideoReady = (event: YouTubeEvent) => {
    if (isFunction(onLoaded)) {
      onLoaded(event)
    }

    setIsLoading?.(false)

    event.target.pauseVideo()
  }

  const formattedPlayingTime = formatDuration(currentPlayingTime * 1000, {
    leading: true,
  })
  const formattedDuration = formatDuration(duration * 1000, { leading: true })

  const fileName =
    url?.split('/').pop() || $t?.('general.the_file').toLocaleLowerCase() || ''

  return (
    <div
      id={id}
      className={cj(
        'item-center relative flex flex-col size-full justify-center rounded-sm',
        type === 'video' ? 'p-0' : 'p-2',
        isUrlValid && isEmpty(error)
          ? ''
          : 'flex-col items-center justify-center gap-1 py-1 pb-3',
      )}
    >
      {isUrlValid && isEmpty(error) ? (
        <>
          {type === 'audio' && (
            <div
              className={cj(
                'flex flex-row gap-2 items-center w-full',
                isLoading || videoMetadata.loading || !isEmpty(error)
                  ? 'hidden'
                  : '',
              )}
            >
              <Dropdown
                className={interactive ? undefined : '[&>button>*]:text-dark'}
                disabled={!interactive}
                trigger={
                  <Typography variant="caption-bold">
                    x&nbsp;{playbackRate}
                  </Typography>
                }
              >
                {PLAYBACK_RATES.map((rate) => (
                  <DropdownItem
                    key={rate}
                    active={rate === playbackRate}
                    disabled={!interactive}
                    onClick={() => updatePlaybackRate(rate)}
                  >
                    <Typography
                      variant={rate === playbackRate ? 'body-1-bold' : 'body-1'}
                    >
                      x&nbsp;{rate}
                    </Typography>
                  </DropdownItem>
                ))}
              </Dropdown>

              {isNumber(currentPlayingTime) && currentPlayingTime >= 0 && (
                <Typography variant="caption-median">
                  {formattedPlayingTime}
                </Typography>
              )}

              <div className="relative w-full flex-1">
                <Slider
                  max={isNumber(duration) && duration > 0 ? duration : 100}
                  value={
                    isNumber(duration) && duration > 0 ? currentPlayingTime : 0
                  }
                  onValueChange={handleSeek}
                />
              </div>

              {isNumber(duration) && duration > 0 && (
                <Typography variant="caption-median">
                  {formattedDuration}
                </Typography>
              )}

              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio
                ref={audioPlayerRef}
                className={cn(
                  'h-4 w-full flex-1 block',
                  '[&::-webkit-media-controls-enclosure]:rounded-xs [&::-webkit-media-controls-enclosure]:bg-transparent [&::-webkit-media-controls-panel]:px-0 [&::-webkit-media-controls-play-button]:hidden [&::-internal-media-controls-overflow-button]:bg-primary',
                  fullWidth ? 'w-full' : '',
                  className,
                )}
                preload="metadata"
                src={url}
                onCanPlayThrough={handleMediaCanPlay}
                onLoadedData={handleMediaLoaded}
                onPlaying={handleMediaPlaying}
                onStalled={handleMediaWaiting}
                onWaiting={handleMediaWaiting}
              />

              <Button
                icon={
                  isPlaying ? (
                    isWaiting ? (
                      <Loader size="xs" />
                    ) : (
                      <PauseIcon />
                    )
                  ) : (
                    <PlayIcon />
                  )
                }
                iconOnly
                label={$t?.(isPlaying ? 'general.pause' : 'general.play')}
                onClick={togglePlay}
              />
            </div>
          )}

          {type === 'video' && (
            <div
              className={cj(
                'w-full rounded-sm',
                isYoutube ? '' : 'h-auto',
                isLoading || videoMetadata.loading || !isEmpty(error)
                  ? 'hidden'
                  : '',
              )}
            >
              {isYoutube && (
                <YouTube
                  className="mb-[-6px] h-fit w-full rounded-sm"
                  opts={{
                    playerVars: {
                      controls: 1,
                    },
                    width: '100%',
                  }}
                  videoId={videoId}
                  onReady={handleYoutubeVideoReady}
                />
              )}

              {isLoom &&
                !videoMetadata.loading &&
                !videoMetadata.error &&
                !isEmpty(videoMetadata.result?.html) && (
                  <div
                    className="h-fit w-full rounded-sm [&>div]:rounded-sm  [&>div>iframe]:rounded-sm"
                    dangerouslySetInnerHTML={{
                      __html: videoMetadata.result?.html ?? '',
                    }}
                  />
                )}

              {isNativeVideo && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                  className="size-full rounded-sm block"
                  controls
                  playsInline
                  preload="metadata"
                  src={url}
                  onCanPlayThrough={handleMediaCanPlay}
                  onError={handleMediaError}
                  onLoadedData={handleMediaLoaded}
                  onPlaying={handleMediaPlaying}
                  onStalled={handleMediaWaiting}
                  onWaiting={handleMediaWaiting}
                />
              )}
            </div>
          )}

          {type === 'file' && (
            <div className="flex flex-row justify-between items-center w-full">
              <Link
                className="flex flex-row gap-3 items-center no-underline w-full flex-1"
                href={url}
                target="_blank"
              >
                <Typography variant="body-1-bold">
                  {$t?.('edition.file.download', { fileName })}
                </Typography>
              </Link>

              <Button
                href={url}
                icon={<DownloadIcon />}
                iconOnly
                label={$t?.('edition.file.download', { fileName })}
                target="_blank"
              />
            </div>
          )}
        </>
      ) : (
        (!isUrlValid || !isEmpty(error)) && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            alt=""
            className="h-20"
            src={
              isUrlValid && isEmpty(error)
                ? url
                : `${assetsURLs.assets}/images/OopsSnowie.png`
            }
          />
        )
      )}

      {((withLoader && (isLoading || videoMetadata.loading)) ||
        !isUrlValid ||
        !isEmpty(error)) && (
        <div className="flex items-center justify-center px-3">
          {(!isLoading && !isUrlValid) ||
            (!isEmpty(error) && $t !== null && (
              <DisplayError
                display
                message={
                  isEmpty(error.trim())
                    ? $t?.(`editor.${type}.error.loading`)
                    : $t?.(`editor.${type}.error.loading_with_message`, {
                        errorMessage: error,
                      })
                }
              />
            ))}

          {(isLoading || videoMetadata.loading) && isEmpty(error) && (
            <div className="flex flex-col gap-1">
              <Loader />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
